import React, { useState, useEffect } from 'react';
import {
  MapPinIcon,
  UserIcon,
  ClockIcon,
  CalendarDaysIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Modal from './ui/Modal';
import { useAuth } from '../context/AuthContext';
import { bookingAPI } from '../services/api';
import toast from 'react-hot-toast';
import ConfirmationModal from './ConfirmationModal';
import ServiceSelectionModal from './ServiceSelectionModal';
import { useNavigate } from "react-router-dom";

const SessionDetailModal = ({ session, isOpen, onClose, onBookingSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientServices, setClientServices] = useState([]);
  const [activeServices, setActiveServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [userWaitlist, setUserWaitlist] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'book' or 'waitlist'
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'success'
  });
  const { isAuthenticated, user } = useAuth();

  const navigate = useNavigate();

  // Fetch user's client services and bookings when modal opens
  // Fetch user's client services and bookings when modal opens
  const fetchUserData = async () => {
    if (!isAuthenticated || !isOpen) return;

    try {
      setLoadingServices(true);
      setLoadingBookings(true);

      const [services, activeServices, bookings, waitlist] = await Promise.all([
        bookingAPI.getMyClientServices(),
        bookingAPI.getMyActiveClientServices(),
        bookingAPI.getMyBookings(),
        bookingAPI.getMyWaitlist()
      ]);

      setClientServices(Array.isArray(services) ? services : (services.results || []));
      setActiveServices(Array.isArray(activeServices) ? activeServices : (activeServices.results || []));
      setUserBookings(Array.isArray(bookings) ? bookings : (bookings.results || []));
      setUserWaitlist(Array.isArray(waitlist) ? waitlist : (waitlist.results || []));
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Erreur lors du chargement de vos données');
    } finally {
      setLoadingServices(false);
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [isAuthenticated, isOpen]);

  // Check if user is already booked or waitlisted for this session
  const getUserBookingStatus = () => {
    if (!isAuthenticated || loadingBookings || !session) {
      return { isBooked: false, isWaitlisted: false, booking: null, waitlistEntry: null };
    }

    // Check for confirmed booking
    const confirmedBooking = userBookings.find(
      booking => booking.session?.id === session.id && booking.status === 'confirmed'
    );

    if (confirmedBooking) {
      return { isBooked: true, isWaitlisted: false, booking: confirmedBooking, waitlistEntry: null };
    }

    // Check for waitlist entry
    const waitlistEntry = userWaitlist.find(
      entry => entry.session?.id === session.id
    );

    if (waitlistEntry) {
      return { isBooked: false, isWaitlisted: true, booking: null, waitlistEntry: waitlistEntry };
    }

    return { isBooked: false, isWaitlisted: false, booking: null, waitlistEntry: null };
  };

  const bookingStatus = getUserBookingStatus();

  if (!session) return null;

  const handleReserve = () => {
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour réserver');
      return;
    }

    // Check if user has active services
    if (activeServices.length === 0) {
      toast.error('Vous devez avoir un service actif pour réserver une séance');
      return;
    }

    // If user has multiple active services, show selection modal
    if (activeServices.length > 1) {
      setPendingAction('book');
      setShowServiceSelection(true);
      return;
    }

    // If user has only one active service, proceed directly
    proceedWithBooking(activeServices[0]);
  };

  const proceedWithBooking = (selectedService) => {
    const sessionDate = new Date(session.date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const sessionTime = `${session.start_time?.slice(0, 5)} - ${session.end_time?.slice(0, 5)}`;

    setConfirmationModal({
      isOpen: true,
      title: 'Confirmer la réservation',
      message: (
        <div>
          <p className="mb-3">Voulez-vous confirmer cette réservation ?</p>
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p><strong>Date:</strong> {sessionDate}</p>
            <p><strong>Heure:</strong> {sessionTime}</p>
            <p><strong>Coach:</strong> {session.coach?.user?.full_name}</p>
            <p><strong>Studio:</strong> {session.location?.name}</p>
            <p><strong>Service:</strong> {selectedService.service_type.name}</p>
          </div>
        </div>
      ),
      type: 'success',
      onConfirm: async () => {
        setConfirmationModal({ ...confirmationModal, isOpen: false });
        setIsLoading(true);

        try {
          const result = await bookingAPI.createBooking(session.id, selectedService.id);

          if (result.waitlist) {
            toast.success(`Ajouté à la liste d'attente (position ${result.waitlist.position})`);
          } else {
            toast.success('Réservation confirmée !');
          }

          await fetchUserData(); // Refresh user data
          onBookingSuccess();
          onClose();
        } catch (error) {
          toast.error(error.message || 'Erreur lors de la réservation');
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleWaitlist = () => {
    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour rejoindre la liste d\'attente');
      return;
    }

    // Check if user has active services
    if (activeServices.length === 0) {
      toast.error('Vous devez avoir un service actif pour rejoindre la liste d\'attente');
      return;
    }

    // If user has multiple active services, show selection modal
    if (activeServices.length > 1) {
      setPendingAction('waitlist');
      setShowServiceSelection(true);
      return;
    }

    // If user has only one active service, proceed directly
    proceedWithWaitlist(activeServices[0]);
  };

  const proceedWithWaitlist = async (selectedService) => {
    setIsLoading(true);
    try {
      const result = await bookingAPI.joinWaitlist(session.id, selectedService.id);

      if (result.waitlist) {
        toast.success(`Ajouté à la liste d'attente (position ${result.waitlist.position})`);
      } else {
        toast.success('Ajouté à la liste d\'attente !');
      }

      await fetchUserData(); // Refresh user data
      onBookingSuccess();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'ajout à la liste d\'attente');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (session.can_reserve) {
      return {
        icon: CheckCircleIcon,
        text: 'Disponible',
        color: 'text-green-600',
        bgColor: 'bg-green-50'
      };
    } else if (session.can_waitlist) {
      return {
        icon: ClipboardDocumentListIcon,
        text: 'Liste d\'attente disponible',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50'
      };
    } else {
      return {
        icon: XCircleIcon,
        text: 'Non disponible',
        color: 'text-red-600',
        bgColor: 'bg-red-50'
      };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Détails de la séance"
    >
      <div className="space-y-6">
        {/* Session Info */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <CalendarDaysIcon className="h-5 w-5 text-gray-500" />
            <span className="text-gray-900 font-medium">
              {format(new Date(session.date), 'EEEE d MMMM yyyy', { locale: fr })}
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <ClockIcon className="h-5 w-5 text-gray-500" />
            <span className="text-gray-900 font-medium">
              {session.start_time} - {session.end_time}
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <MapPinIcon className="h-5 w-5 text-gray-500" />
            <div>
              <div className="text-gray-900 font-medium">{session.location.name}</div>
              <div className="text-sm text-gray-600">{session.location.address}</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <UserIcon className="h-5 w-5 text-gray-500" />
            <div>
              <div className="text-gray-900 font-medium">{session.coach.user.full_name}</div>
              <div className="text-sm text-gray-600">
                Spécialités: {
                  Array.isArray(session.coach.specialties)
                    ? session.coach.specialties.join(', ')
                    : session.coach.specialties || 'Non spécifié'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Booking Status */}
        <div className={`p-4 rounded-lg ${statusInfo.bgColor}`}>
          <div className="flex items-center space-x-3">
            <StatusIcon className={`h-6 w-6 ${statusInfo.color}`} />
            <span className={`font-medium ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
          </div>
        </div>

        {/* Booking Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <UsersIcon className="h-5 w-5 text-primary-600" />
              <span className="text-sm text-gray-600">Réservations</span>
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {session.current_bookings}/{session.max_capacity}
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <ClipboardDocumentListIcon className="h-5 w-5 text-yellow-600" />
              <span className="text-sm text-gray-600">Liste d'attente</span>
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900">
              {session.waitlist_count || 0}/3
            </div>
          </div>
        </div>

        {/* Warning for time restrictions */}
        {!session.can_reserve && !session.can_waitlist && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Réservation non disponible</p>
                <p className="mt-1">
                  Les réservations doivent être effectuées au moins 2 heures à l'avance.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Service Status Check */}
        {isAuthenticated && (loadingServices || loadingBookings) && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">
              {loadingServices && loadingBookings ? 'Chargement de vos données...' :
               loadingServices ? 'Chargement de vos services...' :
               'Vérification de vos réservations...'}
            </p>
          </div>
        )}

        {isAuthenticated && !loadingServices && clientServices.filter(s => s.status === 'active').length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Aucun service actif</p>
                <p className="mt-1">
                  Vous devez avoir un pack ou un abonnement actif pour réserver une séance.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* User Booking Status */}
        {bookingStatus.isBooked && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Réservation confirmée</p>
                <p className="mt-1">
                  Vous avez déjà une réservation confirmée pour cette séance.
                </p>
              </div>
            </div>
          </div>
        )}

        {bookingStatus.isWaitlisted && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ClipboardDocumentListIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Dans la liste d'attente</p>
                <p className="mt-1">
                  Vous êtes déjà dans la liste d'attente pour cette séance
                  {bookingStatus.waitlistEntry?.position && ` (position ${bookingStatus.waitlistEntry.position})`}.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {/* Show buttons only if user is not already booked or waitlisted */}
          {!bookingStatus.isBooked && !bookingStatus.isWaitlisted && (
            <>
              {/* Show "Réserver maintenant" only if session has available spots */}
              {session.can_reserve && session.current_bookings < session.max_capacity && (
                <button
                  onClick={handleReserve}
                  disabled={isLoading || !isAuthenticated || loadingServices || loadingBookings || clientServices.filter(s => s.status === 'active').length === 0}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  {isLoading ? 'Réservation...' : 'Réserver maintenant'}
                </button>
              )}

              {/* Show "Rejoindre la liste d'attente" only if session is full but waitlist available */}
              {session.can_waitlist && session.current_bookings >= session.max_capacity && (
                <button
                  onClick={handleWaitlist}
                  disabled={isLoading || !isAuthenticated || loadingServices || loadingBookings || clientServices.filter(s => s.status === 'active').length === 0}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-300 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  {isLoading ? 'Ajout...' : 'Rejoindre la liste d\'attente'}
                </button>
              )}

              {/* Show "Non disponible" if neither booking nor waitlist is available */}
              {!session.can_reserve && !session.can_waitlist && (
                <button
                  disabled
                  className="flex-1 bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium cursor-not-allowed"
                >
                  Non disponible
                </button>
              )}
            </>
          )}

          {/* Show status button when user is already booked/waitlisted */}
          {(bookingStatus.isBooked || bookingStatus.isWaitlisted) && (
            <button
              disabled
              className="flex-1 bg-gray-100 text-gray-600 py-3 px-4 rounded-lg font-medium cursor-not-allowed"
            >
              {bookingStatus.isBooked ? 'Déjà réservé' : 'Déjà en liste d\'attente'}
            </button>
          )}
        </div>

        {!isAuthenticated && (
          <div className="text-center text-sm text-gray-600">
            <p>
              Vous devez être{' '}
              <button
                onClick={() => {
                  onClose();
                  // Navigate to login - you can implement this
                  navigate('/login');
                }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                connecté
              </button>
              {' '}pour réserver une séance.
            </p>
          </div>
        )}
      </div>

      {/* Service Selection Modal */}
      <ServiceSelectionModal
        isOpen={showServiceSelection}
        onClose={() => {
          setShowServiceSelection(false);
          setPendingAction(null);
        }}
        services={activeServices}
        onSelectService={(selectedService) => {
          setShowServiceSelection(false);
          if (pendingAction === 'book') {
            proceedWithBooking(selectedService);
          } else if (pendingAction === 'waitlist') {
            proceedWithWaitlist(selectedService);
          }
          setPendingAction(null);
        }}
        title={pendingAction === 'book' ? 'Choisir un service pour la réservation' : 'Choisir un service pour la liste d\'attente'}
        message={pendingAction === 'book'
          ? 'Sélectionnez le pack ou abonnement à utiliser pour cette réservation :'
          : 'Sélectionnez le pack ou abonnement à utiliser pour rejoindre la liste d\'attente :'
        }
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        loading={isLoading}
      />
    </Modal>
  );
};

export default SessionDetailModal;
